import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Upload, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const CategoryImportButton = () => {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleImport = async () => {
    try {
      setImporting(true);
      setResult(null);
      
      toast.info('Fetching CSV data...');
      
      // Fetch CSV from public folder
      const response = await fetch('/MinorSoccerTalks_simplified.csv');
      if (!response.ok) {
        throw new Error('Failed to fetch CSV file');
      }
      
      const csvData = await response.text();
      toast.info('Starting category import...');
      
      // Call import edge function
      const importResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-categories`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ csvData })
        }
      );
      
      if (!importResponse.ok) {
        const errorData = await importResponse.json();
        throw new Error(errorData.error || 'Import failed');
      }
      
      const importResult = await importResponse.json();
      setResult(importResult);
      
      if (importResult.success) {
        toast.success(`Import complete! Imported ${importResult.summary.imported} categories`);
      } else {
        toast.error('Import completed with errors');
      }
      
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import categories');
      setResult({ error: error.message });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Import Soccer Categories</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Import all soccer categories from the MinorSoccerTalks CSV file. This will create USA/Canada states, age groups, and leagues.
      </p>
      
      <Button 
        onClick={handleImport} 
        disabled={importing}
        className="mb-4"
      >
        {importing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Importing...
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            Import Categories
          </>
        )}
      </Button>

      {result && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          {result.success ? (
            <div className="space-y-2">
              <div className="flex items-center text-green-600">
                <CheckCircle className="mr-2 h-4 w-4" />
                <span className="font-semibold">Import Successful!</span>
              </div>
              <div className="text-sm space-y-1">
                <div>✅ Imported: {result.summary.imported}</div>
                <div>⏭️ Skipped: {result.summary.skipped}</div>
                <div>❌ Errors: {result.summary.errors}</div>
                <div>📊 Total: {result.summary.total}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center text-red-600">
              <XCircle className="mr-2 h-4 w-4" />
              <span className="font-semibold">Import Failed: {result.error}</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
